import { formatDistanceToNowStrict } from "date-fns"
import type { Locale as DateFnsLocale } from "date-fns"
// `date-fns` is `sideEffects: false` and the `locale` barrel is ESM re-exports,
// so the bundler keeps only `enUS` + `fr` — not every locale date-fns ships.
// The subpath's `.d.cts` types trip the strict `import/extensions` rule (same
// cause as Base UI's `.mts` subpaths in `components/segmented-control.tsx`);
// disable only that rule, on this one import.
// eslint-disable-next-line import/extensions
import { enUS, fr } from "date-fns/locale"

import type { Locale } from "@/types"

/**
 * Relative-time formatting — WHICH HELPER FOR WHICH SHAPE.
 *
 * | Shape you need | Use |
 * |---|---|
 * | Compact feed copy from i18n keys: « il y a 5 min », "Just now", an absolute date after 7 days | `formatRelativeTime` |
 * | An exact strict duration, with or without a suffix, clamped to the past: « il y a 2 secondes », « 3 jours », « il y a 1 mois » | `formatStrictDistance` |
 * | A native `Intl` phrase with calendar words, minute/hour/day units only, future allowed: « cette minute-ci », « hier », « avant-hier », « dans 6 jours » | `formatRelativeIntl` |
 *
 * Why row 3 is `Intl` and not date-fns: date-fns has no `numeric: "auto"`, so
 * « hier » would become « il y a 1 jour » and « cette minute-ci » would become
 * « il y a 0 secondes ».
 *
 * ⚠️ Three shapes, three helpers. Do not add another inline `Intl` copy: pick a
 * row, and if no row fits, add a row here in the same change.
 * Known deliberate inline `Intl.RelativeTimeFormat` uses, each commented at the
 * call with the reason it is not `formatRelativeIntl`:
 * `components/crew/kudo-card.tsx` (day-only, absolute from day 7),
 * `components/creator/mission-drop-card.tsx` (calendar-day delta),
 * `components/crew/segment-relative-date.tsx` (hour/day/month/year units).
 *
 * Every helper takes the language as an ARGUMENT, read by the caller from
 * i18next (`toAppLocale(useTranslation().i18n.language)` or the page's
 * `locale: Locale` prop). Never read `document.documentElement.lang`: there is
 * no `document` during the SSR render.
 */

/**
 * OKS-393 — Shared compact relative-time formatter.
 *
 * Extracted from `notification-row.tsx` (OKS-36) so the notifications
 * surface AND the recent-activity feed render timestamps identically:
 * "Just now", "5m ago", "3h ago", "2d ago", falling back to the
 * localised date for anything older than 7 days.
 *
 * The i18n key prefix is configurable so each surface keeps a
 * self-contained dictionary (`notifications.time.*` /
 * `recent_activity.time.*`) without coupling one namespace to another.
 * Both prefixes expose the same leaf keys: `just_now`, `minutes_ago`,
 * `hours_ago`, `days_ago` (the latter three take a `count`).
 *
 * @param iso       ISO-8601 timestamp string.
 * @param t         react-i18next translate function.
 * @param language  active locale (for the >7d `toLocaleDateString` fallback).
 * @param keyPrefix i18n namespace holding the `time.*` leaves.
 * @returns localised relative-time label.
 */
export function formatRelativeTime(
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
  language: string,
  keyPrefix = "notifications.time",
): string {
  const created = new Date(iso)
  const diffMs = Date.now() - created.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return t(`${keyPrefix}.just_now`)
  if (diffMin < 60) return t(`${keyPrefix}.minutes_ago`, { count: diffMin })
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return t(`${keyPrefix}.hours_ago`, { count: diffHr })
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return t(`${keyPrefix}.days_ago`, { count: diffDay })
  return created.toLocaleDateString(language)
}

/**
 * Maps every app `Locale` to its date-fns locale. A `Record`, so adding a
 * member to `Locale` fails `tsc` here until the new language is mapped — no
 * silent English fallback.
 */
const DATE_FNS_LOCALES: Record<Locale, DateFnsLocale> = {
  en: enUS,
  fr,
}

/**
 * OKS-1025 — Strict distance between `iso` and now, in the given language.
 *
 * Wraps date-fns `formatDistanceToNowStrict` with an explicit locale; before
 * OKS-1025 every caller omitted it, so French pages read "2 seconds ago".
 *
 * - `addSuffix: true` → « il y a 2 secondes » / "2 seconds ago" (a moment).
 * - no suffix        → « 3 jours » / "3 days" (a duration, e.g. an age chip).
 *
 * A timestamp in the future — only possible when the client clock is behind
 * the server's — is clamped to now, so a past event never reads
 * « dans 5 secondes » / "in 5 seconds" (it reads « il y a 0 secondes »).
 *
 * Pure and SSR-safe: no `document`, no `window`, no i18next import.
 *
 * @param iso     ISO-8601 timestamp string.
 * @param locale  the active app language (`"en" | "fr"`), from i18next.
 * @param options `addSuffix` — append « il y a » / "ago" (default `false`).
 * @returns the localised distance, or `iso` unchanged if it cannot be
 *          formatted (unparseable input degrades instead of crashing a page).
 */
export function formatStrictDistance(
  iso: string,
  locale: Locale,
  options: { addSuffix?: boolean } = {},
): string {
  try {
    const then = new Date(iso).getTime()
    const clamped = new Date(Math.min(then, Date.now()))
    return formatDistanceToNowStrict(clamped, {
      addSuffix: options.addSuffix ?? false,
      locale: DATE_FNS_LOCALES[locale],
    })
  } catch {
    return iso
  }
}

/**
 * OKS-1032 — Native `Intl.RelativeTimeFormat` phrase for `iso`, relative to
 * now, in the given language. Extracted byte-for-byte from the five inline
 * `relativeTime` copies (Admin overview, Agency + Brand team tables, the admin
 * invite digest card, the pending first-admin invite card).
 *
 * - Rounded minutes below 60, then rounded hours below 24, then days. Never
 *   week, month or year: −400 days reads « il y a 400 jours » / "400 days ago".
 * - `numeric: "auto"` gives the calendar words: 0 min → « cette minute-ci » /
 *   "this minute", −1 day → « hier » / "yesterday", −2 days → « avant-hier »
 *   / "2 days ago", +1 day → « demain » / "tomorrow".
 * - The future is NOT clamped (unlike `formatStrictDistance`): an invite's
 *   `expires_at` reads « dans 6 jours » / "in 6 days".
 * - Each unit is `Math.round`ed on its own, so −30 s is « cette minute-ci »
 *   but +30 s is « dans 1 minute ».
 *
 * Pure and SSR-safe: no `document`, no `window`, no i18next import.
 *
 * @param iso     ISO-8601 timestamp string.
 * @param locale  the active app language (`"en" | "fr"`) — pass
 *                `toAppLocale(i18n.language)`.
 * @returns the localised phrase, or `iso` unchanged when it cannot be parsed or
 *          `Intl.RelativeTimeFormat` is unavailable.
 */
export function formatRelativeIntl(iso: string, locale: Locale): string {
  if (typeof Intl === "undefined" || !Intl.RelativeTimeFormat) return iso
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diffMs = then - Date.now()
  const diffMin = Math.round(diffMs / (60 * 1000))
  const diffHr = Math.round(diffMs / (60 * 60 * 1000))
  const diffDay = Math.round(diffMs / (24 * 60 * 60 * 1000))

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, "minute")
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, "hour")
  return rtf.format(diffDay, "day")
}
